import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, LogIn, Trash2, Building2, Search } from "lucide-react";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const PLAN_LABELS = { solo: "Solo · ₹799", starter: "Starter · ₹1,299", growth: "Growth · ₹2,499",
                      professional: "Professional · ₹4,999", enterprise: "Enterprise · ₹7,999" };

export default function AgencyClients() {
  const { switchOrg } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", entity_type: "company", plan: "starter", gstin: "", pan: "", contact_email: "",
  });

  const reload = () => {
    setLoading(true);
    api.get("/agency/clients")
      .then((r) => setClients(r.data?.clients || []))
      .catch(() => toast.error("Could not load clients"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/agency/clients", form);
      toast.success(`Client "${form.name}" added`);
      setOpenAdd(false);
      setForm({ name: "", entity_type: "company", plan: "starter", gstin: "", pan: "", contact_email: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add client");
    } finally { setSaving(false); }
  };

  const impersonate = async (c) => {
    try {
      await api.post(`/agency/impersonate/${c.id}`);
      await switchOrg(c.id);
      toast.success(`Signed in as ${c.name}`);
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
    }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Its books are archived (not permanently deleted).`)) return;
    try {
      await api.delete(`/agency/clients/${c.id}`);
      toast.success("Client archived");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = clients.filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="agency-clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Client companies</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl mt-2">Every client, one workspace</h1>
          <p className="text-sm text-foreground/70 mt-1">Click <b>Sign in</b> to jump into any client's books — you land back here anytime.</p>
        </div>
        <Button onClick={() => setOpenAdd(true)} className="rounded-sm bg-logo-gradient text-white border-0 gap-1.5" data-testid="agency-add-client-btn">
          <Plus className="w-4 h-4" /> Add client company
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
                 className="rounded-sm pl-9" data-testid="agency-clients-search" />
        </div>
        <div className="text-xs text-foreground/60 font-mono">{filtered.length} of {clients.length}</div>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        {loading && <div className="p-8 text-center text-sm text-foreground/60">Loading…</div>}

        {!loading && filtered.length === 0 && (
          <div className="p-14 text-center">
            <Building2 className="w-10 h-10 mx-auto text-foreground/30" />
            <div className="mt-3 font-heading font-semibold text-lg">No clients yet</div>
            <div className="text-sm text-foreground/60 mt-1">Add your first client company — they get their own books, GSTINs and users.</div>
            <Button onClick={() => setOpenAdd(true)} className="mt-5 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="agency-empty-add-btn">
              Add client
            </Button>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 border-b border-border">
              <tr>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3">GSTIN</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Revenue (MTD)</th>
                <th className="text-right p-3">AR</th>
                <th className="text-right p-3">Filings due</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-border/60 hover:bg-muted/30" data-testid={`agency-client-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 font-mono text-xs text-foreground/70">{c.gstin || "—"}</td>
                  <td className="p-3 text-xs">{PLAN_LABELS[c.plan] || c.plan}</td>
                  <td className="p-3 text-right font-mono">{inr(c.revenue_mtd)}</td>
                  <td className="p-3 text-right font-mono">{inr(c.ar_outstanding)}</td>
                  <td className="p-3 text-right">
                    {c.pending_filings > 0
                      ? <span className="text-amber-600 font-mono">{c.pending_filings}</span>
                      : <span className="text-emerald-600 font-mono">0</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => impersonate(c)}
                            className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1"
                            data-testid={`agency-signin-${c.id}`}>
                      <LogIn className="w-3.5 h-3.5" /> Sign in
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeClient(c)}
                            className="rounded-sm ml-2 text-destructive hover:text-destructive"
                            data-testid={`agency-remove-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add client dialog */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="agency-add-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add a client company</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">Client name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Acme Traders Pvt Ltd" className="rounded-sm mt-1" data-testid="agency-new-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Entity type</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="agency-new-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proprietorship">Proprietorship</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="llp">LLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="agency-new-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLAN_LABELS).map(([id, label]) =>
                      <SelectItem key={id} value={id}>{label}/mo</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">GSTIN (optional)</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                       className="rounded-sm mt-1 font-mono" data-testid="agency-new-gstin" />
              </div>
              <div>
                <Label className="text-xs">PAN (optional)</Label>
                <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                       className="rounded-sm mt-1 font-mono" data-testid="agency-new-pan" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Contact email (optional)</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                     className="rounded-sm mt-1" placeholder="finance@client.com" data-testid="agency-new-email" />
              <div className="text-[10px] text-foreground/60 mt-1">You can invite the client's team from Settings → Team once inside.</div>
            </div>
            <div className="p-3 rounded-sm bg-accent/5 border border-accent/25 text-xs text-foreground/75">
              The plan cost will roll up to your agency's monthly bill. You can change plans per client any time.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpenAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-sm bg-logo-gradient text-white border-0" data-testid="agency-new-submit">
                {saving ? "Adding…" : "Add client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
